import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../config/api';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Label } from '../ui/label';
import { FONT_MAP } from '../ThemeInjector';
import { FONT_PRESETS, FONT_SLOTS, FONT_SLOT_DEFAULTS, resolveFontFamily } from '../../config/fonts';
import { Save, Loader2, Palette, Type, Square, Eye, RotateCcw, Plus, Trash2, Star, ArrowUp, ArrowDown, List, RotateCw, Upload, X, FileType, Heart } from 'lucide-react';

const PRESETS = [
  { id: 'embers', label: 'Embers', primary: '#FF3B30', accent: '#DAA520', desc: 'Fire red + gold' },
  { id: 'midnight', label: 'Midnight', primary: '#6366F1', accent: '#8B5CF6', desc: 'Indigo + purple' },
  { id: 'ocean', label: 'Ocean', primary: '#0EA5E9', accent: '#06B6D4', desc: 'Deep blue + cyan' },
  { id: 'toxic', label: 'Toxic', primary: '#22C55E', accent: '#84CC16', desc: 'Green + lime' },
  { id: 'rose', label: 'Rose', primary: '#F43F5E', accent: '#EC4899', desc: 'Rose + pink' },
  { id: 'monochrome', label: 'Mono', primary: '#E5E5E5', accent: '#737373', desc: 'Clean grayscale' },
];

const FONT_OPTIONS = [
  { id: 'modern-grotesk', label: 'Modern', heading: 'Cabinet Grotesk', body: 'General Sans' },
  { id: 'classic-serif', label: 'Classic', heading: 'Playfair Display', body: 'Source Serif Pro' },
  { id: 'tech-mono', label: 'Tech', heading: 'JetBrains Mono', body: 'Inter' },
  { id: 'street', label: 'Street', heading: 'Oswald', body: 'Barlow' },
];

const RADIUS_OPTIONS = [
  { value: 0, label: 'Sharp', desc: '0px' },
  { value: 4, label: 'Subtle', desc: '4px' },
  { value: 8, label: 'Modern', desc: '8px' },
  { value: 999, label: 'Pill', desc: 'Full' },
];

const SECTIONS = [
  { id: 'hero', label: 'Hero / Heading' },
  { id: 'marquee', label: 'Scrolling Marquee' },
  { id: 'tracks', label: 'Music Player / Tracks' },
  { id: 'slideshow', label: 'Image Slideshow' },
  { id: 'videos', label: 'Music Videos' },
  { id: 'about', label: 'Bio / About' },
  { id: 'featured', label: 'Featured Artists' },
  { id: 'beats', label: 'Beat Store' },
  { id: 'free_media', label: 'Free Media' },
  { id: 'merch', label: 'Merch Store' },
  { id: 'follow', label: 'Follow / Social' },
  { id: 'subscribe', label: 'Email Subscribe' },
  { id: 'donations', label: 'Donations' },
  { id: 'raffles', label: 'Raffles / Giveaways' },
  { id: 'thank_you', label: 'Thank You Note' },
];

// Sections that can be reordered on the home page (everything except fixed-top Hero/Marquee).
// Keep in sync with the renderers map in /app/frontend/src/pages/Home.js
const REORDERABLE_SECTIONS = [
  { id: 'tracks', label: 'Music Player / Tracks' },
  { id: 'slideshow', label: 'Image Slideshow' },
  { id: 'videos', label: 'Music Videos' },
  { id: 'about', label: 'Bio / About' },
  { id: 'featured', label: 'Featured Artists' },
  { id: 'beats', label: 'Beat Store' },
  { id: 'free_media', label: 'Free Media' },
  { id: 'merch', label: 'Merch Store' },
  { id: 'follow', label: 'Follow / Social' },
  { id: 'donations', label: 'Donations' },
  { id: 'subscribe', label: 'Email Subscribe' },
  { id: 'raffles', label: 'Raffles / Giveaways' },
  { id: 'thank_you', label: 'Thank You Note' },
];
const DEFAULT_SECTION_ORDER = REORDERABLE_SECTIONS.map(s => s.id);

export default function CustomizePanel({ siteSettings, onSettingsUpdate }) {
  const existing = siteSettings?.customize || {};
  const [primary, setPrimary] = useState(existing.primary || '#FF3B30');
  const [accent, setAccent] = useState(existing.accent || '#DAA520');
  const [radius, setRadius] = useState(existing.radius ?? 0);
  const [font, setFont] = useState(existing.font || 'modern-grotesk');
  const [sections, setSections] = useState(existing.sections || {});
  const [fontSlots, setFontSlots] = useState(existing.font_slots || { ...FONT_SLOT_DEFAULTS });
  const [fontSlotStyles, setFontSlotStyles] = useState(existing.font_slot_styles || {});
  const [customFonts, setCustomFonts] = useState([]);
  const [uploadingFont, setUploadingFont] = useState(false);
  const [buttonStyle, setButtonStyle] = useState(existing.button_style || { shape: 'square', hover: 'fill', shadow: 0, uppercase: true });
  const [borderStyle, setBorderStyle] = useState(existing.border_style || { style: 'solid', thickness: 1, color: '#262626', card_radius: 0 });
  const [interaction, setInteraction] = useState(existing.interaction || { selection_color: '#FF3B30', selection_text: '#FFFFFF', scroll_color: '#FF3B30', scroll_width: 10 });
  const [showFontAdvanced, setShowFontAdvanced] = useState({});
  const [sectionOrder, setSectionOrder] = useState(() => {
    const saved = existing.section_order;
    if (!Array.isArray(saved) || !saved.length) return DEFAULT_SECTION_ORDER;
    // Append any newly-added default sections not yet saved
    return [...saved, ...DEFAULT_SECTION_ORDER.filter(id => !saved.includes(id))];
  });
  const [marqueeColor, setMarqueeColor] = useState(existing.marquee_color || '#DAA520');
  const [heartbeatColor, setHeartbeatColor] = useState(existing.heartbeat_color || '#FF3B30');
  const [headerBg, setHeaderBg] = useState(existing.header_bg || '#0A0A0A');
  const [headerText, setHeaderText] = useState(existing.header_text || '#FFFFFF');
  const [navColor, setNavColor] = useState(existing.nav_color || '#DAA520');
  const [ember1, setEmber1] = useState(existing.ember_color_1 || '#FF7814');
  const [ember2, setEmber2] = useState(existing.ember_color_2 || '#DAA520');
  const [ember3, setEmber3] = useState(existing.ember_color_3 || '#B41414');
  const [btnBg, setBtnBg] = useState(existing.btn_bg || '#FF3B30');
  const [btnText, setBtnText] = useState(existing.btn_text || '#FFFFFF');
  const [customPresets, setCustomPresets] = useState(existing.custom_presets || []);
  const [presetName, setPresetName] = useState('');

  // Hero title customization (studio name + WORLD)
  const [studioTitle, setStudioTitle] = useState(existing.studio_title || 'DUGOWTBOI ENTERTAINMENT™');
  const [studioTitleSize, setStudioTitleSize] = useState(existing.studio_title_size || 40);
  const [studioTitleGradient, setStudioTitleGradient] = useState(existing.studio_title_gradient || ['#FF3B30', '#DAA520', '#FFFFFF', '#DAA520', '#FF3B30']);
  const [worldText, setWorldText] = useState(existing.world_text || 'WORLD');
  const [worldSize, setWorldSize] = useState(existing.world_size || 56);
  const [worldGradient, setWorldGradient] = useState(existing.world_gradient || ['#FF3B30', '#FF6347', '#DAA520', '#FFD700', '#FFFFFF']);
  const [worldSyncTrack, setWorldSyncTrack] = useState(existing.world_sync_track || false);
  const [worldTrackPrefix, setWorldTrackPrefix] = useState(existing.world_track_prefix || '');

  // Thank You Note customization
  const [thankYouTitle, setThankYouTitle] = useState(existing.thank_you_title || 'Thank You For Your Support');
  const [thankYouBody, setThankYouBody] = useState(existing.thank_you_body || 'Every purchase directly supports an independent artist creating from a wheelchair. No label. No team. Just passion, persistence, and a second life worth living. I.O.M.S.L');

  const existingTitles = siteSettings?.section_titles || {};
  const [sectionTitles, setSectionTitles] = useState({
    music: existingTitles.music || 'BANGER BOOTH',
    music_label: existingTitles.music_label || 'NOW PLAYING',
    videos: existingTitles.videos || 'VISUALS',
    videos_label: existingTitles.videos_label || 'WATCH',
    about: existingTitles.about || 'ABOUT ME',
    about_label: existingTitles.about_label || 'THE ARTIST',
    featured: existingTitles.featured || 'FEATURED ARTISTS',
    featured_label: existingTitles.featured_label || 'SPOTLIGHT',
    beats: existingTitles.beats || 'SLAP SECTION',
    beats_label: existingTitles.beats_label || 'PRODUCERS',
    free_media: existingTitles.free_media || 'FREE MEDIA',
    free_media_label: existingTitles.free_media_label || 'FREE',
    merch: existingTitles.merch || 'MERCH',
    merch_label: existingTitles.merch_label || 'OFFICIAL',
    follow: existingTitles.follow || 'FOLLOW ME',
    follow_label: existingTitles.follow_label || 'STAY CONNECTED',
    donations: existingTitles.donations || 'SUPPORT THE MOVEMENT',
    donations_label: existingTitles.donations_label || 'SUPPORT THE MOVEMENT',
    subscribe: existingTitles.subscribe || 'SUBSCRIBE',
    subscribe_label: existingTitles.subscribe_label || 'STAY UPDATED',
  });
  const [saving, setSaving] = useState(false);

  // Load custom fonts once when the panel mounts
  useEffect(() => {
    axios.get(`${API}/fonts`).then(({ data }) => setCustomFonts(data || [])).catch(() => {});
  }, []);

  const uploadFontFile = async (file) => {
    setUploadingFont(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const isZip = /\.zip$/i.test(file.name);
      const endpoint = isZip ? '/fonts/upload-zip' : '/fonts/upload';
      const { data } = await api.post(`${API}${endpoint}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (isZip) {
        toast.success(`Extracted ${data.saved_count} font${data.saved_count === 1 ? '' : 's'} from zip`);
      } else {
        toast.success(`Uploaded ${data.family}`);
      }
      const refreshed = await axios.get(`${API}/fonts`).then(r => r.data).catch(() => []);
      setCustomFonts(refreshed);
      // Inject @font-face immediately so the new fonts show up in dropdowns/preview
      (isZip ? (data.saved || []) : [data]).forEach(f => {
        if (!f?.url || !f?.family) return;
        const id = `cf-${f.id}`;
        if (document.getElementById(id)) return;
        const style = document.createElement('style');
        style.id = id;
        const format = f.format === 'ttf' ? 'truetype' : f.format === 'otf' ? 'opentype' : f.format;
        style.textContent = `@font-face { font-family: '${f.family.replace(/'/g, "\\'")}'; src: url('${f.url}') format('${format}'); font-display: swap; }`;
        document.head.appendChild(style);
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    setUploadingFont(false);
  };

  const deleteCustomFont = async (id) => {
    if (!confirm('Delete this font? Anywhere it\'s used will fall back to the default.')) return;
    try {
      await api.delete(`${API}/fonts/${id}`);
      setCustomFonts(customFonts.filter(f => f.id !== id));
      toast.success('Font deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  useEffect(() => {
    const c = siteSettings?.customize || {};
    const t = siteSettings?.section_titles || {};
    setPrimary(c.primary || '#FF3B30');
    setAccent(c.accent || '#DAA520');
    setRadius(c.radius ?? 0);
    setFont(c.font || 'modern-grotesk');
    setSections(c.sections || {});
    setFontSlots(c.font_slots || { ...FONT_SLOT_DEFAULTS });
    setFontSlotStyles(c.font_slot_styles || {});
    setButtonStyle(c.button_style || { shape: 'square', hover: 'fill', shadow: 0, uppercase: true });
    setBorderStyle(c.border_style || { style: 'solid', thickness: 1, color: '#262626', card_radius: 0 });
    setInteraction(c.interaction || { selection_color: '#FF3B30', selection_text: '#FFFFFF', scroll_color: '#FF3B30', scroll_width: 10 });
    const savedOrder = c.section_order;
    if (Array.isArray(savedOrder) && savedOrder.length) {
      setSectionOrder([...savedOrder, ...DEFAULT_SECTION_ORDER.filter(id => !savedOrder.includes(id))]);
    } else {
      setSectionOrder(DEFAULT_SECTION_ORDER);
    }
    setMarqueeColor(c.marquee_color || '#DAA520');
    setHeartbeatColor(c.heartbeat_color || '#FF3B30');
    setHeaderBg(c.header_bg || '#0A0A0A');
    setHeaderText(c.header_text || '#FFFFFF');
    setNavColor(c.nav_color || '#DAA520');
    setEmber1(c.ember_color_1 || '#FF7814');
    setEmber2(c.ember_color_2 || '#DAA520');
    setEmber3(c.ember_color_3 || '#B41414');
    setBtnBg(c.btn_bg || '#FF3B30');
    setBtnText(c.btn_text || '#FFFFFF');
    setCustomPresets(c.custom_presets || []);
    setStudioTitle(c.studio_title || 'DUGOWTBOI ENTERTAINMENT™');
    setStudioTitleSize(c.studio_title_size || 40);
    setStudioTitleGradient(c.studio_title_gradient || ['#FF3B30', '#DAA520', '#FFFFFF', '#DAA520', '#FF3B30']);
    setWorldText(c.world_text || 'WORLD');
    setWorldSize(c.world_size || 56);
    setWorldGradient(c.world_gradient || ['#FF3B30', '#FF6347', '#DAA520', '#FFD700', '#FFFFFF']);
    setWorldSyncTrack(c.world_sync_track || false);
    setWorldTrackPrefix(c.world_track_prefix || '');
    setThankYouTitle(c.thank_you_title || 'Thank You For Your Support');
    setThankYouBody(c.thank_you_body || 'Every purchase directly supports an independent artist creating from a wheelchair. No label. No team. Just passion, persistence, and a second life worth living. I.O.M.S.L');
    setSectionTitles(prev => ({
      ...prev,
      music: t.music || 'BANGER BOOTH', music_label: t.music_label || 'NOW PLAYING',
      videos: t.videos || 'VISUALS', videos_label: t.videos_label || 'WATCH',
      about: t.about || 'ABOUT ME', about_label: t.about_label || 'THE ARTIST',
      featured: t.featured || 'FEATURED ARTISTS', featured_label: t.featured_label || 'SPOTLIGHT',
      beats: t.beats || 'SLAP SECTION', beats_label: t.beats_label || 'PRODUCERS',
      free_media: t.free_media || 'FREE MEDIA', free_media_label: t.free_media_label || 'FREE',
      merch: t.merch || 'MERCH', merch_label: t.merch_label || 'OFFICIAL',
      follow: t.follow || 'FOLLOW ME', follow_label: t.follow_label || 'STAY CONNECTED',
      donations: t.donations || 'SUPPORT THE MOVEMENT', donations_label: t.donations_label || 'SUPPORT THE MOVEMENT',
      subscribe: t.subscribe || 'SUBSCRIBE', subscribe_label: t.subscribe_label || 'STAY UPDATED',
    }));
  }, [siteSettings?.customize]);

  // Live preview
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary', primary);
    root.style.setProperty('--accent', accent);
    root.style.setProperty('--radius', `${radius}px`);
    root.style.setProperty('--marquee-color', marqueeColor);
    root.style.setProperty('--heartbeat-color', heartbeatColor);
    root.style.setProperty('--header-bg', headerBg);
    root.style.setProperty('--header-text', headerText);
    root.style.setProperty('--nav-color', navColor);
    root.style.setProperty('--ember-color-1', ember1);
    root.style.setProperty('--ember-color-2', ember2);
    root.style.setProperty('--ember-color-3', ember3);
    root.style.setProperty('--btn-bg', btnBg);
    root.style.setProperty('--btn-text', btnText);
    const hexToRgb = (hex) => { const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex); return r ? `${parseInt(r[1],16)}, ${parseInt(r[2],16)}, ${parseInt(r[3],16)}` : '255, 59, 48'; };
    root.style.setProperty('--primary-rgb', hexToRgb(primary));
    root.style.setProperty('--accent-rgb', hexToRgb(accent));
    root.style.setProperty('--heartbeat-rgb', hexToRgb(heartbeatColor));
    if (FONT_MAP[font]) {
      root.style.setProperty('--font-heading', FONT_MAP[font].heading);
      root.style.setProperty('--font-body', FONT_MAP[font].body);
      document.body.style.fontFamily = FONT_MAP[font].body;
    }
  }, [primary, accent, radius, font, marqueeColor, heartbeatColor, headerBg, headerText, navColor, ember1, ember2, ember3, btnBg, btnText]);

  const applyPreset = (p) => { setPrimary(p.primary); setAccent(p.accent); };
  const toggleSection = (id) => setSections(prev => ({ ...prev, [id]: prev[id] === false ? true : false }));
  const resetDefaults = () => {
    setPrimary('#FF3B30'); setAccent('#DAA520'); setRadius(0); setFont('modern-grotesk'); setSections({});
    setMarqueeColor('#DAA520'); setHeartbeatColor('#FF3B30'); setHeaderBg('#0A0A0A'); setHeaderText('#FFFFFF');
    setNavColor('#DAA520'); setBtnBg('#FF3B30'); setBtnText('#FFFFFF');
    toast.info('Reset to defaults — save to apply');
  };

  const getAllColors = () => ({ primary, accent, marquee_color: marqueeColor, heartbeat_color: heartbeatColor, header_bg: headerBg, header_text: headerText, nav_color: navColor, ember_color_1: ember1, ember_color_2: ember2, ember_color_3: ember3, btn_bg: btnBg, btn_text: btnText });

  const saveCustomization = async () => {
    setSaving(true);
    try {
      const payload = {
        customize: {
          ...(siteSettings?.customize || {}),
          ...getAllColors(),
          radius, font, sections, custom_presets: customPresets,
          section_order: sectionOrder,
          studio_title: studioTitle,
          studio_title_size: studioTitleSize,
          studio_title_gradient: studioTitleGradient,
          world_text: worldText,
          world_size: worldSize,
          world_gradient: worldGradient,
          world_sync_track: worldSyncTrack,
          world_track_prefix: worldTrackPrefix,
          thank_you_title: thankYouTitle,
          thank_you_body: thankYouBody,
          font_slots: fontSlots,
          font_slot_styles: fontSlotStyles,
          button_style: buttonStyle,
          border_style: borderStyle,
          interaction,
        },
        section_titles: sectionTitles
      };
      const { data } = await api.put(`${API}/site-settings`, payload);
      if (onSettingsUpdate) onSettingsUpdate(data);
      toast.success('Customization saved!');
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  // Reusable color picker row
  const ColorRow = ({ label, value, onChange, id, note }) => (
    <div>
      <label className="text-[10px] text-[#888] uppercase block mb-1">{label} {note && <span className="text-[#444]">{note}</span>}</label>
      <div className="flex items-center gap-1">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent" data-testid={`color-${id}`} />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 bg-[#0A0A0A] border border-[#262626] px-2 py-1.5 text-[10px] font-mono text-white outline-none w-0" style={{ borderRadius: `${radius}px` }} />
      </div>
    </div>
  );

  return (
    <div className="space-y-6 pb-32" data-testid="customize-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-black uppercase tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Customize</h2>
          <p className="text-sm text-[#666] mt-1">All your site settings in one place</p>
        </div>
        <div className="flex gap-2">
          <button onClick={resetDefaults} className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-[#666] border border-[#262626] hover:text-white hover:border-[#444] transition-colors" style={{ borderRadius: `${radius}px` }} data-testid="customize-reset">
            <RotateCcw className="w-3 h-3" /> RESET
          </button>
          <button onClick={saveCustomization} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white transition-all hover:brightness-110 disabled:opacity-50" style={{ background: primary, borderRadius: `${radius}px` }} data-testid="customize-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? 'SAVING...' : 'SAVE'}
          </button>
        </div>
      </div>

      {/* ─── 1. PRESETS ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Palette className="w-4 h-4" style={{ color: primary }} /> 1. Color Presets
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {PRESETS.map(p => (
            <button key={p.id} onClick={() => applyPreset(p)} className={`text-left p-3 border transition-all hover:scale-[1.02] ${primary === p.primary && accent === p.accent ? 'border-white/30 bg-white/5' : 'border-[#262626] bg-[#0A0A0A] hover:border-white/10'}`} style={{ borderRadius: `${radius}px` }} data-testid={`preset-${p.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 rounded-full border border-white/10" style={{ background: p.primary }} />
                <div className="w-4 h-4 rounded-full border border-white/10" style={{ background: p.accent }} />
                <span className="text-xs font-bold">{p.label}</span>
              </div>
              <p className="text-[10px] text-[#666]">{p.desc}</p>
            </button>
          ))}
        </div>
        {customPresets.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#1a1a1a]">
            <span className="text-[10px] text-[#888] uppercase tracking-wider block mb-2">My Saved Presets</span>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {customPresets.map((cp, i) => (
                <div key={i} className="relative group">
                  <button onClick={() => { setPrimary(cp.primary); setAccent(cp.accent); setBtnBg(cp.btn_bg || cp.primary); setBtnText(cp.btn_text || '#FFFFFF'); setMarqueeColor(cp.marquee_color || cp.accent); setHeartbeatColor(cp.heartbeat_color || cp.primary); setHeaderBg(cp.header_bg || '#0A0A0A'); setHeaderText(cp.header_text || '#FFFFFF'); setNavColor(cp.nav_color || cp.accent); setEmber1(cp.ember_color_1 || '#FF7814'); setEmber2(cp.ember_color_2 || '#DAA520'); setEmber3(cp.ember_color_3 || '#B41414'); }}
                    className="w-full text-left p-3 border border-[#262626] bg-[#0A0A0A] hover:border-white/10 transition-all" style={{ borderRadius: `${radius}px` }} data-testid={`my-preset-${i}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full" style={{ background: cp.primary }} />
                      <div className="w-4 h-4 rounded-full" style={{ background: cp.accent }} />
                      <span className="text-xs font-bold">{cp.name}</span>
                    </div>
                  </button>
                  <button onClick={() => setCustomPresets(customPresets.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center bg-red-600 text-white rounded transition-opacity" data-testid={`delete-preset-${i}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-3 flex gap-2">
          <input type="text" value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="Preset name..." className="flex-1 bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-xs text-white outline-none" style={{ borderRadius: `${radius}px` }} data-testid="preset-name-input" />
          <button onClick={() => {
            if (!presetName.trim()) { toast.error('Enter a name'); return; }
            if (customPresets.length >= 10) { toast.error('Max 10'); return; }
            setCustomPresets([...customPresets, { name: presetName.trim(), ...getAllColors() }]);
            setPresetName(''); toast.success('Preset saved! Click SAVE to keep.');
          }} className="flex items-center gap-1 px-3 py-2 text-xs font-bold uppercase bg-[#141414] border border-[#262626] hover:border-[#444]" style={{ borderRadius: `${radius}px` }} data-testid="save-preset-btn">
            <Star className="w-3 h-3" /> SAVE PRESET
          </button>
        </div>
      </section>

      {/* ─── 2. SITE-WIDE COLORS ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Palette className="w-4 h-4" style={{ color: accent }} /> 2. Site-Wide Colors
        </h3>
        <p className="text-[10px] text-[#666] mb-3">These two affect everything — buttons, links, badges, progress bars, waves</p>
        <div className="grid grid-cols-2 gap-3">
          <ColorRow label="Primary" value={primary} onChange={setPrimary} id="primary" />
          <ColorRow label="Accent" value={accent} onChange={setAccent} id="accent" />
        </div>
        <div className="mt-3 flex gap-2"><div className="flex-1 h-2" style={{ background: primary, borderRadius: `${radius}px` }} /><div className="flex-1 h-2" style={{ background: accent, borderRadius: `${radius}px` }} /></div>
      </section>

      {/* ─── 3. HEADER & NAV BAR ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Eye className="w-4 h-4" style={{ color: accent }} /> 3. Header & Nav Bar
        </h3>
        <p className="text-[10px] text-[#666] mb-3">Top navigation (STORE, cart, SHARE, QR, ADMIN links)</p>
        <div className="grid grid-cols-3 gap-3">
          <ColorRow label="Background" value={headerBg} onChange={setHeaderBg} id="header-bg" />
          <ColorRow label="Text Color" value={headerText} onChange={setHeaderText} id="header-text" />
          <ColorRow label="Nav Links" value={navColor} onChange={setNavColor} id="nav-color" />
        </div>
      </section>

      {/* ─── 4. SCROLLING MARQUEE ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Type className="w-4 h-4" style={{ color: accent }} /> 4. Scrolling Marquee
        </h3>
        <p className="text-[10px] text-[#666] mb-3">The large scrolling "ENTERTAINMENT" text banner</p>
        <ColorRow label="Marquee Text" value={marqueeColor} onChange={setMarqueeColor} id="marquee" />
      </section>

      {/* ─── 5. HERO SECTION ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Eye className="w-4 h-4" style={{ color: primary }} /> 5. Hero Section
        </h3>
        <p className="text-[10px] text-[#666] mb-3">"STREAM & SLAP..." heading, subheading, EKG heartbeat, embers</p>
        <div className="grid grid-cols-2 gap-3">
          <ColorRow label="Heading Text" value={headerText} onChange={setHeaderText} id="hero-heading" />
          <ColorRow label="EKG / Heartbeat" value={heartbeatColor} onChange={setHeartbeatColor} id="heartbeat" />
          <ColorRow label="Ember 1" value={ember1} onChange={setEmber1} id="ember-1" />
          <ColorRow label="Ember 2" value={ember2} onChange={setEmber2} id="ember-2" />
          <ColorRow label="Ember 3" value={ember3} onChange={setEmber3} id="ember-3" />
        </div>
      </section>

      {/* ─── 5b. STUDIO TITLE & WORLD TEXT ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Type className="w-4 h-4" style={{ color: primary }} /> 5b. Studio Title & WORLD Text
        </h3>
        <p className="text-[10px] text-[#666] mb-3">The big "DUGOWTBOI ENTERTAINMENT™" + "WORLD" above the EKG line. Fully customizable.</p>

        {/* Studio Title */}
        <div className="mb-4 p-3 bg-[#0A0A0A] border border-[#1a1a1a]" style={{ borderRadius: `${radius}px` }}>
          <span className="text-[10px] text-[#888] uppercase tracking-wider block mb-2">Studio Title</span>
          <input type="text" value={studioTitle} onChange={(e) => setStudioTitle(e.target.value)}
            placeholder="DUGOWTBOI ENTERTAINMENT™"
            className="w-full bg-[#141414] border border-[#262626] px-2 py-1.5 text-xs text-white outline-none uppercase font-bold mb-2" style={{ borderRadius: `${radius}px` }}
            data-testid="studio-title-input" />
          <div className="mb-2">
            <div className="flex justify-between items-center">
              <label className="text-[10px] text-[#888]">Font Size</label>
              <span className="text-[10px] text-[#DAA520] font-mono">{studioTitleSize}px</span>
            </div>
            <input type="range" min="16" max="96" value={studioTitleSize}
              onChange={(e) => setStudioTitleSize(parseInt(e.target.value))}
              className="w-full mt-1 accent-[#DAA520] h-2" data-testid="studio-title-size" />
          </div>
          <label className="text-[10px] text-[#888] uppercase tracking-wider block mb-1">Gradient Colors ({studioTitleGradient.length} stops)</label>
          <div className="grid grid-cols-3 gap-1.5">
            {studioTitleGradient.map((c, i) => (
              <div key={i} className="flex items-center gap-1">
                <input type="color" value={c}
                  onChange={(e) => { const n = [...studioTitleGradient]; n[i] = e.target.value; setStudioTitleGradient(n); }}
                  className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent"
                  data-testid={`studio-grad-${i}`} />
                <input type="text" value={c}
                  onChange={(e) => { const n = [...studioTitleGradient]; n[i] = e.target.value; setStudioTitleGradient(n); }}
                  className="flex-1 bg-[#141414] border border-[#262626] px-1 py-1 text-[9px] font-mono text-white outline-none w-0" style={{ borderRadius: `${radius}px` }} />
                {studioTitleGradient.length > 2 && (
                  <button onClick={() => setStudioTitleGradient(studioTitleGradient.filter((_, idx) => idx !== i))}
                    className="text-[#666] hover:text-red-400 text-xs px-1" data-testid={`studio-grad-del-${i}`}>×</button>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2 items-center">
            {studioTitleGradient.length < 6 && (
              <button onClick={() => setStudioTitleGradient([...studioTitleGradient, '#FFFFFF'])}
                className="text-[9px] px-2 py-1 bg-[#141414] border border-[#262626] text-[#A3A3A3] hover:border-[#DAA520]" style={{ borderRadius: `${radius}px` }} data-testid="studio-grad-add">
                + Add Stop
              </button>
            )}
            <div className="flex-1 h-4 border border-[#262626]"
              style={{ background: `linear-gradient(135deg, ${studioTitleGradient.join(',')})`, borderRadius: `${radius}px` }} />
          </div>
          <div className="mt-3 text-center p-2 bg-black/40 border border-[#1a1a1a]" style={{ borderRadius: `${radius}px` }}>
            <span className="uppercase font-bold tracking-[0.2em]"
              style={{
                fontFamily: "'Orbitron', sans-serif",
                fontSize: `${Math.min(studioTitleSize, 28)}px`,
                backgroundImage: `linear-gradient(135deg, ${studioTitleGradient.join(',')})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                color: 'transparent',
              }}>{studioTitle || 'PREVIEW'}</span>
          </div>
        </div>

        {/* WORLD Text */}
        <div className="p-3 bg-[#0A0A0A] border border-[#1a1a1a]" style={{ borderRadius: `${radius}px` }}>
          <span className="text-[10px] text-[#888] uppercase tracking-wider block mb-2">WORLD Text (revealed by EKG)</span>

          {/* Sync with currently playing track */}
          <div className="flex items-center justify-between bg-[#141414] border border-[#262626] px-3 py-2 mb-2" style={{ borderRadius: `${radius}px` }}>
            <div>
              <label className="text-[11px] text-white font-bold block">Sync with playing track</label>
              <span className="text-[9px] text-[#666]">When a song plays, the EKG reveals its title instead of the static text</span>
            </div>
            <button onClick={() => setWorldSyncTrack(!worldSyncTrack)}
              className={`w-10 h-5 rounded-full relative transition-all shrink-0 ml-2 ${worldSyncTrack ? 'bg-[#DAA520]' : 'bg-[#333]'}`}
              data-testid="world-sync-track-toggle">
              <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-all ${worldSyncTrack ? 'left-5' : 'left-0.5'}`} />
            </button>
          </div>

          {worldSyncTrack && (
            <div className="mb-2">
              <label className="text-[9px] text-[#888] uppercase tracking-wider block mb-0.5">Prefix (optional, e.g. "NOW PLAYING:")</label>
              <input type="text" value={worldTrackPrefix} onChange={(e) => setWorldTrackPrefix(e.target.value)}
                placeholder="(none)"
                className="w-full bg-[#141414] border border-[#262626] px-2 py-1.5 text-xs text-white outline-none uppercase" style={{ borderRadius: `${radius}px` }}
                data-testid="world-track-prefix" />
            </div>
          )}

          <label className="text-[9px] text-[#888] uppercase tracking-wider block mb-0.5">{worldSyncTrack ? 'Fallback Text (when nothing is playing)' : 'Text'}</label>
          <input type="text" value={worldText} onChange={(e) => setWorldText(e.target.value)}
            placeholder="WORLD"
            className="w-full bg-[#141414] border border-[#262626] px-2 py-1.5 text-xs text-white outline-none uppercase font-bold mb-2" style={{ borderRadius: `${radius}px` }}
            data-testid="world-text-input" />
          <div className="mb-2">
            <div className="flex justify-between items-center">
              <label className="text-[10px] text-[#888]">Font Size</label>
              <span className="text-[10px] text-[#DAA520] font-mono">{worldSize}px</span>
            </div>
            <input type="range" min="20" max="140" value={worldSize}
              onChange={(e) => setWorldSize(parseInt(e.target.value))}
              className="w-full mt-1 accent-[#DAA520] h-2" data-testid="world-size" />
          </div>
          <label className="text-[10px] text-[#888] uppercase tracking-wider block mb-1">Gradient Colors ({worldGradient.length} stops)</label>
          <div className="grid grid-cols-3 gap-1.5">
            {worldGradient.map((c, i) => (
              <div key={i} className="flex items-center gap-1">
                <input type="color" value={c}
                  onChange={(e) => { const n = [...worldGradient]; n[i] = e.target.value; setWorldGradient(n); }}
                  className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent"
                  data-testid={`world-grad-${i}`} />
                <input type="text" value={c}
                  onChange={(e) => { const n = [...worldGradient]; n[i] = e.target.value; setWorldGradient(n); }}
                  className="flex-1 bg-[#141414] border border-[#262626] px-1 py-1 text-[9px] font-mono text-white outline-none w-0" style={{ borderRadius: `${radius}px` }} />
                {worldGradient.length > 2 && (
                  <button onClick={() => setWorldGradient(worldGradient.filter((_, idx) => idx !== i))}
                    className="text-[#666] hover:text-red-400 text-xs px-1" data-testid={`world-grad-del-${i}`}>×</button>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2 items-center">
            {worldGradient.length < 6 && (
              <button onClick={() => setWorldGradient([...worldGradient, '#FFFFFF'])}
                className="text-[9px] px-2 py-1 bg-[#141414] border border-[#262626] text-[#A3A3A3] hover:border-[#DAA520]" style={{ borderRadius: `${radius}px` }} data-testid="world-grad-add">
                + Add Stop
              </button>
            )}
            <div className="flex-1 h-4 border border-[#262626]"
              style={{ background: `linear-gradient(90deg, ${worldGradient.join(',')})`, borderRadius: `${radius}px` }} />
          </div>
          <div className="mt-3 text-center p-2 bg-black/40 border border-[#1a1a1a]" style={{ borderRadius: `${radius}px` }}>
            <span className="uppercase tracking-[0.25em]"
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: `${Math.min(worldSize, 40)}px`,
                backgroundImage: `linear-gradient(90deg, ${worldGradient.join(',')})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                color: 'transparent',
              }}>{worldText || 'WORLD'}</span>
          </div>
        </div>
      </section>

      {/* ─── 6. BUTTONS ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Square className="w-4 h-4" style={{ color: primary }} /> 6. Buttons (All Sections)
        </h3>
        <p className="text-[10px] text-[#666] mb-3">BROWSE TRACKS, BUY NOW, ADD TO CART, and all action buttons site-wide</p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <ColorRow label="Button Background" value={btnBg} onChange={setBtnBg} id="btn-bg" />
          <ColorRow label="Button Text" value={btnText} onChange={setBtnText} id="btn-text" />
        </div>
        <label className="text-[10px] text-[#888] uppercase block mb-1.5">Button Shape</label>
        <div className="grid grid-cols-4 gap-2 mb-3">
          {RADIUS_OPTIONS.map(r => (
            <button key={r.value} onClick={() => setRadius(r.value)} className={`p-2 border text-center transition-all ${radius === r.value ? 'border-white/30 bg-white/5' : 'border-[#262626] hover:border-white/10'}`} style={{ borderRadius: `${r.value}px` }} data-testid={`radius-${r.value}`}>
              <span className="text-xs font-bold block">{r.label}</span>
              <span className="text-[9px] text-[#666]">{r.desc}</span>
            </button>
          ))}
        </div>
        <button className="px-6 py-2.5 text-sm font-bold uppercase tracking-wider" style={{ background: btnBg, color: btnText, borderRadius: `${radius}px` }}>PREVIEW BUTTON</button>
      </section>

      {/* ─── 7. TYPOGRAPHY ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Type className="w-4 h-4" style={{ color: accent }} /> 7. Typography
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {FONT_OPTIONS.map(f => (
            <button key={f.id} onClick={() => setFont(f.id)} className={`text-left p-3 border transition-all ${font === f.id ? 'border-white/30 bg-white/5' : 'border-[#262626] hover:border-white/10'}`} style={{ borderRadius: `${radius}px` }} data-testid={`font-${f.id}`}>
              <p className="text-sm font-bold mb-0.5" style={{ fontFamily: FONT_MAP[f.id]?.heading }}>{f.label}</p>
              <p className="text-[10px] text-[#666]" style={{ fontFamily: FONT_MAP[f.id]?.body }}>{f.heading} + {f.body}</p>
            </button>
          ))}
        </div>
      </section>

      {/* ─── 8. SECTION NAMES ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Type className="w-4 h-4" style={{ color: primary }} /> 8. Section Names
        </h3>
        <p className="text-[10px] text-[#666] mb-3">Rename every section on your site. Each has a small label + main title.</p>
        <div className="space-y-3">
          {[
            { key: 'music', label: 'Music Player', defaults: ['NOW PLAYING', 'BANGER BOOTH'] },
            { key: 'videos', label: 'Videos', defaults: ['WATCH', 'VISUALS'] },
            { key: 'about', label: 'Bio / About', defaults: ['THE ARTIST', 'ABOUT ME'] },
            { key: 'featured', label: 'Featured Artists', defaults: ['SPOTLIGHT', 'FEATURED ARTISTS'] },
            { key: 'beats', label: 'Beat Store', defaults: ['PRODUCERS', 'SLAP SECTION'] },
            { key: 'free_media', label: 'Free Media', defaults: ['FREE', 'FREE MEDIA'] },
            { key: 'merch', label: 'Merch Store', defaults: ['OFFICIAL', 'MERCH'] },
            { key: 'follow', label: 'Social / Follow', defaults: ['STAY CONNECTED', 'FOLLOW ME'] },
            { key: 'donations', label: 'Donations', defaults: ['SUPPORT THE MOVEMENT', 'SUPPORT THE MOVEMENT'] },
            { key: 'subscribe', label: 'Email Subscribe', defaults: ['STAY UPDATED', 'SUBSCRIBE'] },
          ].map(s => (
            <div key={s.key} className="bg-[#0A0A0A] border border-[#1a1a1a] p-3" style={{ borderRadius: `${radius}px` }}>
              <span className="text-[10px] text-[#666] uppercase tracking-wider block mb-2">{s.label}</span>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] text-[#555] block mb-0.5">Small Label</label>
                  <input type="text" value={sectionTitles[`${s.key}_label`] || ''} onChange={(e) => setSectionTitles(prev => ({ ...prev, [`${s.key}_label`]: e.target.value }))}
                    placeholder={s.defaults[0]}
                    className="w-full bg-[#141414] border border-[#262626] px-2 py-1.5 text-xs text-white outline-none uppercase" style={{ borderRadius: `${radius}px` }}
                    data-testid={`title-${s.key}-label`} />
                </div>
                <div>
                  <label className="text-[9px] text-[#555] block mb-0.5">Main Title</label>
                  <input type="text" value={sectionTitles[s.key] || ''} onChange={(e) => setSectionTitles(prev => ({ ...prev, [s.key]: e.target.value }))}
                    placeholder={s.defaults[1]}
                    className="w-full bg-[#141414] border border-[#262626] px-2 py-1.5 text-xs text-white outline-none uppercase font-bold" style={{ borderRadius: `${radius}px` }}
                    data-testid={`title-${s.key}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── 9. SECTION VISIBILITY ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Eye className="w-4 h-4" style={{ color: accent }} /> 9. Section Visibility
        </h3>
        <p className="text-[10px] text-[#666] mb-3">Show or hide sections on your public site</p>
        <div className="space-y-1">
          {SECTIONS.map(s => {
            const visible = sections[s.id] !== false;
            return (
              <button key={s.id} onClick={() => toggleSection(s.id)} className={`w-full flex items-center justify-between px-3 py-2 border transition-colors ${visible ? 'border-[#262626] hover:border-white/10' : 'border-[#262626] bg-[#0A0A0A] opacity-50'}`} style={{ borderRadius: `${radius}px` }} data-testid={`section-toggle-${s.id}`}>
                <span className="text-xs font-bold">{s.label}</span>
                <div className="w-8 h-4 rounded-full flex items-center transition-colors" style={{ background: visible ? primary : '#333', justifyContent: visible ? 'flex-end' : 'flex-start', padding: '2px' }}>
                  <div className="w-3 h-3 rounded-full bg-white shadow-sm" />
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* ─── 10. SECTION ORDER ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <List className="w-4 h-4" style={{ color: accent }} /> 10. Section Order
        </h3>
        <p className="text-[10px] text-[#666] mb-3">
          Rearrange how sections appear on your homepage. Hero & marquee are always at the top; equalizer & footer always at the bottom.
        </p>
        <div className="space-y-1.5">
          {sectionOrder.map((id, idx) => {
            const meta = REORDERABLE_SECTIONS.find(s => s.id === id);
            if (!meta) return null;
            const visible = sections[id] !== false;
            const isFirst = idx === 0;
            const isLast = idx === sectionOrder.length - 1;
            const move = (dir) => {
              const next = [...sectionOrder];
              const target = idx + dir;
              if (target < 0 || target >= next.length) return;
              [next[idx], next[target]] = [next[target], next[idx]];
              setSectionOrder(next);
            };
            return (
              <div key={id}
                className="flex items-center gap-2 px-2 py-2 border border-[#262626] bg-[#0A0A0A]"
                style={{ borderRadius: `${radius}px`, opacity: visible ? 1 : 0.5 }}
                data-testid={`section-order-row-${id}`}>
                <span className="text-[10px] text-[#666] font-mono w-5 text-right shrink-0">{idx + 1}.</span>
                <span className="flex-1 text-xs font-bold truncate">
                  {meta.label}
                  {!visible && <span className="text-[9px] text-[#666] uppercase tracking-wider ml-2">(hidden)</span>}
                </span>
                <button
                  onClick={() => move(-1)}
                  disabled={isFirst}
                  className={`w-8 h-8 flex items-center justify-center border transition-colors ${isFirst ? 'border-[#1a1a1a] text-[#333] cursor-not-allowed' : 'border-[#262626] text-[#A3A3A3] hover:border-white hover:text-white'}`}
                  style={{ borderRadius: `${radius}px` }}
                  data-testid={`section-order-up-${id}`}
                  title="Move up">
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => move(1)}
                  disabled={isLast}
                  className={`w-8 h-8 flex items-center justify-center border transition-colors ${isLast ? 'border-[#1a1a1a] text-[#333] cursor-not-allowed' : 'border-[#262626] text-[#A3A3A3] hover:border-white hover:text-white'}`}
                  style={{ borderRadius: `${radius}px` }}
                  data-testid={`section-order-down-${id}`}
                  title="Move down">
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
        <button
          onClick={() => setSectionOrder(DEFAULT_SECTION_ORDER)}
          className="mt-3 text-[10px] text-[#A3A3A3] hover:text-white uppercase tracking-wider flex items-center gap-1.5 transition-colors"
          data-testid="section-order-reset">
          <RotateCw className="w-3 h-3" /> Reset to default order
        </button>
      </section>

      {/* ─── 11. FONTS ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Type className="w-4 h-4" style={{ color: accent }} /> 11. Fonts
        </h3>
        <p className="text-[10px] text-[#666] mb-4">
          Pick a font for each role. Every text on the site uses one of these four slots — change them globally here, or upload your own.
        </p>

        {/* Font slot pickers */}
        <div className="space-y-3 mb-5">
          {FONT_SLOTS.map(slot => {
            const currentValue = fontSlots[slot.id] || FONT_SLOT_DEFAULTS[slot.id];
            const previewFamily = resolveFontFamily(currentValue, customFonts, 'sans-serif');
            return (
              <div key={slot.id} className="bg-[#0A0A0A] border border-[#1a1a1a] p-3" style={{ borderRadius: `${radius}px` }}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <span className="text-xs font-bold uppercase tracking-wider block" style={{ color: primary }}>{slot.label}</span>
                    <span className="text-[10px] text-[#666]">{slot.desc}</span>
                  </div>
                  <select
                    value={currentValue}
                    onChange={(e) => setFontSlots({ ...fontSlots, [slot.id]: e.target.value })}
                    className="bg-[#141414] border border-[#262626] text-white text-xs px-2 py-2 outline-none min-w-[180px] shrink-0"
                    style={{ borderRadius: `${radius}px` }}
                    data-testid={`font-slot-${slot.id}`}
                  >
                    <optgroup label="Display">
                      {FONT_PRESETS.filter(f => f.category === 'display').map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </optgroup>
                    <optgroup label="Sans-serif">
                      {FONT_PRESETS.filter(f => f.category === 'sans').map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </optgroup>
                    <optgroup label="Serif">
                      {FONT_PRESETS.filter(f => f.category === 'serif').map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </optgroup>
                    <optgroup label="Monospace">
                      {FONT_PRESETS.filter(f => f.category === 'mono').map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </optgroup>
                    <optgroup label="Script / Handwritten">
                      {FONT_PRESETS.filter(f => f.category === 'script').map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </optgroup>
                    {customFonts.length > 0 && (
                      <optgroup label="Your Custom Fonts">
                        {customFonts.map(f => <option key={f.id} value={f.id}>{f.family}</option>)}
                      </optgroup>
                    )}
                  </select>
                </div>
                {/* Live preview */}
                <div className="mt-1 p-2 bg-black/40 border border-[#1a1a1a]" style={{ borderRadius: `${radius}px` }}>
                  <div style={{
                    fontFamily: previewFamily,
                    fontSize: slot.id === 'mono' ? `${14 * (fontSlotStyles[slot.id]?.size_multiplier || 1)}px` : `${20 * (fontSlotStyles[slot.id]?.size_multiplier || 1)}px`,
                    lineHeight: 1.2,
                    fontWeight: fontSlotStyles[slot.id]?.weight || (slot.id === 'heading' ? 900 : 400),
                    letterSpacing: fontSlotStyles[slot.id]?.letter_spacing || 'normal',
                    textTransform: fontSlotStyles[slot.id]?.transform || 'none',
                  }} className="truncate">
                    {slot.id === 'mono' ? 'Price: $19.99 · BPM 140 · 3:28' : 'The quick brown fox jumps — Aa 1234'}
                  </div>
                </div>

                {/* Advanced toggle */}
                <button
                  onClick={() => setShowFontAdvanced({ ...showFontAdvanced, [slot.id]: !showFontAdvanced[slot.id] })}
                  className="mt-2 text-[10px] text-[#A3A3A3] hover:text-white uppercase tracking-wider"
                  data-testid={`font-advanced-toggle-${slot.id}`}
                >
                  {showFontAdvanced[slot.id] ? '− Hide' : '+ Advanced'} (weight · spacing · case · size)
                </button>

                {showFontAdvanced[slot.id] && (
                  <div className="mt-2 grid grid-cols-2 gap-2 p-2 bg-black/30 border border-[#1a1a1a]" style={{ borderRadius: `${radius}px` }}>
                    {/* Weight */}
                    <div>
                      <label className="text-[9px] text-[#888] uppercase tracking-wider block mb-0.5">Weight</label>
                      <select value={fontSlotStyles[slot.id]?.weight || ''}
                        onChange={(e) => setFontSlotStyles({ ...fontSlotStyles, [slot.id]: { ...fontSlotStyles[slot.id], weight: e.target.value } })}
                        className="w-full bg-[#141414] border border-[#262626] text-white text-[10px] px-1.5 py-1 outline-none"
                        style={{ borderRadius: `${radius}px` }}
                        data-testid={`font-weight-${slot.id}`}>
                        <option value="">Default</option>
                        <option value="300">300 Light</option>
                        <option value="400">400 Regular</option>
                        <option value="500">500 Medium</option>
                        <option value="600">600 SemiBold</option>
                        <option value="700">700 Bold</option>
                        <option value="800">800 ExtraBold</option>
                        <option value="900">900 Black</option>
                      </select>
                    </div>
                    {/* Letter spacing */}
                    <div>
                      <label className="text-[9px] text-[#888] uppercase tracking-wider block mb-0.5">Letter Spacing</label>
                      <select value={fontSlotStyles[slot.id]?.letter_spacing || 'normal'}
                        onChange={(e) => setFontSlotStyles({ ...fontSlotStyles, [slot.id]: { ...fontSlotStyles[slot.id], letter_spacing: e.target.value } })}
                        className="w-full bg-[#141414] border border-[#262626] text-white text-[10px] px-1.5 py-1 outline-none"
                        style={{ borderRadius: `${radius}px` }}
                        data-testid={`font-spacing-${slot.id}`}>
                        <option value="-0.05em">Tight</option>
                        <option value="-0.02em">Snug</option>
                        <option value="normal">Normal</option>
                        <option value="0.05em">Relaxed</option>
                        <option value="0.1em">Wide</option>
                        <option value="0.2em">Widest</option>
                      </select>
                    </div>
                    {/* Transform */}
                    <div>
                      <label className="text-[9px] text-[#888] uppercase tracking-wider block mb-0.5">Text Case</label>
                      <select value={fontSlotStyles[slot.id]?.transform || 'none'}
                        onChange={(e) => setFontSlotStyles({ ...fontSlotStyles, [slot.id]: { ...fontSlotStyles[slot.id], transform: e.target.value } })}
                        className="w-full bg-[#141414] border border-[#262626] text-white text-[10px] px-1.5 py-1 outline-none"
                        style={{ borderRadius: `${radius}px` }}
                        data-testid={`font-transform-${slot.id}`}>
                        <option value="none">Normal</option>
                        <option value="uppercase">UPPERCASE</option>
                        <option value="lowercase">lowercase</option>
                        <option value="capitalize">Capitalize</option>
                      </select>
                    </div>
                    {/* Size multiplier */}
                    <div>
                      <div className="flex justify-between items-center">
                        <label className="text-[9px] text-[#888] uppercase tracking-wider">Size</label>
                        <span className="text-[9px] text-[#DAA520] font-mono">{(fontSlotStyles[slot.id]?.size_multiplier || 1).toFixed(2)}×</span>
                      </div>
                      <input type="range" min="0.75" max="1.5" step="0.05"
                        value={fontSlotStyles[slot.id]?.size_multiplier || 1}
                        onChange={(e) => setFontSlotStyles({ ...fontSlotStyles, [slot.id]: { ...fontSlotStyles[slot.id], size_multiplier: parseFloat(e.target.value) } })}
                        className="w-full mt-0.5 accent-[#DAA520] h-1.5"
                        data-testid={`font-size-${slot.id}`} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Upload zone */}
        <div className="border border-dashed border-[#262626] p-4 bg-[#0A0A0A]" style={{ borderRadius: `${radius}px` }}>
          <div className="flex items-center gap-2 mb-2">
            <Upload className="w-4 h-4" style={{ color: accent }} />
            <span className="text-xs font-bold uppercase tracking-wider">Upload Your Own Fonts</span>
          </div>
          <p className="text-[10px] text-[#666] mb-3">
            Accepts <span className="font-mono text-[#A3A3A3]">.ttf .otf .woff .woff2</span> individually, or <span className="font-mono text-[#A3A3A3]">.zip</span> containing many fonts (auto-extracted).
          </p>
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#141414] border border-[#262626] hover:border-white transition-colors cursor-pointer text-xs font-bold uppercase tracking-wider" style={{ borderRadius: `${radius}px` }}>
            <input
              type="file"
              accept=".ttf,.otf,.woff,.woff2,.zip,font/ttf,font/otf,font/woff,font/woff2,application/zip"
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                for (const f of files) await uploadFontFile(f);
                e.target.value = '';
              }}
              className="hidden"
              disabled={uploadingFont}
              data-testid="font-upload-input"
            />
            {uploadingFont ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileType className="w-3.5 h-3.5" />}
            {uploadingFont ? 'Uploading…' : 'Choose Files'}
          </label>
        </div>

        {/* Custom fonts list */}
        {customFonts.length > 0 && (
          <div className="mt-4">
            <span className="text-[10px] text-[#888] uppercase tracking-wider block mb-2">Your Custom Fonts ({customFonts.length})</span>
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {customFonts.map(f => (
                <div key={f.id} className="flex items-center gap-3 px-3 py-2 bg-[#0A0A0A] border border-[#1a1a1a]" style={{ borderRadius: `${radius}px` }} data-testid={`custom-font-${f.id}`}>
                  <div className="flex-1 min-w-0">
                    <div style={{ fontFamily: `'${f.family}', sans-serif` }} className="text-sm truncate">
                      {f.family} <span className="text-[10px] text-[#666] uppercase ml-1 font-mono">.{f.format}</span>
                    </div>
                    <div className="text-[10px] text-[#555] truncate">{f.original_filename}</div>
                  </div>
                  <button
                    onClick={() => deleteCustomFont(f.id)}
                    className="p-1.5 text-[#666] hover:text-red-400 transition-colors shrink-0"
                    title="Delete font"
                    data-testid={`delete-font-${f.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ─── 12. BUTTONS ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Square className="w-4 h-4" style={{ color: primary }} /> 12. Buttons
        </h3>
        <p className="text-[10px] text-[#666] mb-3">Shape, hover behavior, and shadow — applied to every BUY / DONATE / SUBSCRIBE button on the site.</p>

        {/* Shape */}
        <div className="mb-3">
          <label className="text-[10px] text-[#888] uppercase tracking-wider block mb-1.5">Shape</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'square', label: 'Square', preview: { borderRadius: '0px' } },
              { id: 'rounded', label: 'Rounded', preview: { borderRadius: '12px' } },
              { id: 'pill', label: 'Pill', preview: { borderRadius: '9999px' } },
              { id: 'slashed', label: 'Slashed', preview: { clipPath: 'polygon(8% 0, 100% 0, 92% 100%, 0 100%)' } },
              { id: 'cut-corner', label: 'Cut Corner', preview: { clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 0 100%)' } },
              { id: 'notched', label: 'Notched', preview: { clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0 calc(100% - 8px))' } },
              { id: 'chevron', label: 'Chevron', preview: { clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)' } },
              { id: 'tab', label: 'Tab', preview: { borderRadius: '12px 12px 0 0' } },
              { id: 'blob', label: 'Blob', preview: { borderRadius: '60% 40% 50% 50% / 50% 60% 40% 50%' } },
            ].map(opt => (
              <button key={opt.id}
                onClick={() => setButtonStyle({ ...buttonStyle, shape: opt.id })}
                className={`p-3 border-2 text-[10px] font-bold uppercase tracking-wider transition-all ${buttonStyle.shape === opt.id ? 'border-[#DAA520] bg-[#DAA520]/10 text-[#DAA520]' : 'border-[#262626] bg-[#0A0A0A] text-[#A3A3A3] hover:border-[#404040]'}`}
                style={{ borderRadius: `${radius}px` }}
                data-testid={`btn-shape-${opt.id}`}>
                <div className="w-full h-5 mx-auto mb-1" style={{ background: primary, ...opt.preview }} />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Hover style */}
        <div className="mb-3">
          <label className="text-[10px] text-[#888] uppercase tracking-wider block mb-1.5">Hover Effect</label>
          <div className="grid grid-cols-4 gap-2">
            {[
              { id: 'fill', label: 'Fill' },
              { id: 'outline', label: 'Outline' },
              { id: 'glow', label: 'Glow' },
              { id: 'shine', label: 'Shine' },
            ].map(opt => (
              <button key={opt.id}
                onClick={() => setButtonStyle({ ...buttonStyle, hover: opt.id })}
                className={`p-2 border-2 text-[10px] font-bold uppercase tracking-wider transition-all ${buttonStyle.hover === opt.id ? 'border-[#DAA520] bg-[#DAA520]/10 text-[#DAA520]' : 'border-[#262626] bg-[#0A0A0A] text-[#A3A3A3] hover:border-[#404040]'}`}
                style={{ borderRadius: `${radius}px` }}
                data-testid={`btn-hover-${opt.id}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Shadow */}
        <div className="mb-3">
          <div className="flex justify-between items-center">
            <label className="text-[10px] text-[#888] uppercase tracking-wider">Shadow</label>
            <span className="text-[10px] text-[#DAA520] font-mono">{buttonStyle.shadow ?? 0}%</span>
          </div>
          <input type="range" min="0" max="100" value={buttonStyle.shadow ?? 0}
            onChange={(e) => setButtonStyle({ ...buttonStyle, shadow: parseInt(e.target.value) })}
            className="w-full mt-1 accent-[#DAA520] h-2"
            data-testid="btn-shadow" />
        </div>

        {/* Uppercase toggle */}
        <div className="flex items-center justify-between bg-[#0A0A0A] border border-[#1a1a1a] px-3 py-2" style={{ borderRadius: `${radius}px` }}>
          <label className="text-xs font-bold">UPPERCASE text</label>
          <button onClick={() => setButtonStyle({ ...buttonStyle, uppercase: !(buttonStyle.uppercase !== false) })}
            className={`w-10 h-5 rounded-full relative transition-all ${buttonStyle.uppercase !== false ? 'bg-[#DAA520]' : 'bg-[#333]'}`}
            data-testid="btn-uppercase-toggle">
            <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-all ${buttonStyle.uppercase !== false ? 'left-5' : 'left-0.5'}`} />
          </button>
        </div>

        {/* Live preview */}
        <div className="mt-3 p-3 bg-black/40 border border-[#1a1a1a] flex items-center justify-center" style={{ borderRadius: `${radius}px` }}>
          <button
            className="px-5 py-2.5 text-sm font-bold transition-all"
            style={{
              background: primary,
              color: 'white',
              borderRadius: buttonStyle.shape === 'pill' ? '9999px' : buttonStyle.shape === 'rounded' ? '12px' : '0px',
              textTransform: (buttonStyle.uppercase !== false) ? 'uppercase' : 'none',
              letterSpacing: (buttonStyle.uppercase !== false) ? '0.05em' : 'normal',
              boxShadow: buttonStyle.shadow > 0 ? `0 ${Math.round(buttonStyle.shadow / 10)}px ${Math.round(buttonStyle.shadow / 3)}px rgba(0,0,0,${(buttonStyle.shadow / 180).toFixed(3)})` : 'none',
            }}
            data-testid="btn-preview"
          >
            Buy Now Preview
          </button>
        </div>
      </section>

      {/* ─── 12b. THANK YOU NOTE ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }} data-testid="thank-you-editor">
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Heart className="w-4 h-4" style={{ color: accent }} /> 12b. Thank You Note
        </h3>
        <p className="text-[10px] text-[#666] mb-3">
          Custom &ldquo;thank you for your support&rdquo; message shown to fans.
          Show/hide + drag to reposition via section 9 (visibility) and section 10 (order) above.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-[#888] uppercase tracking-wider block mb-1">Title / Heading</label>
            <input value={thankYouTitle} onChange={(e) => setThankYouTitle(e.target.value)}
              placeholder="Thank You For Your Support"
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm"
              data-testid="thank-you-title-input" />
          </div>
          <div>
            <label className="text-[10px] text-[#888] uppercase tracking-wider block mb-1">Body Text</label>
            <textarea value={thankYouBody} onChange={(e) => setThankYouBody(e.target.value)}
              rows={4}
              placeholder="Every purchase directly supports…"
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#DAA520] outline-none px-3 py-2 text-sm resize-y"
              data-testid="thank-you-body-input" />
          </div>
          <div className="border border-[#262626] bg-[#0A0A0A] p-4">
            <p className="text-[9px] uppercase tracking-widest text-[#666] mb-2">Preview</p>
            <p className="font-cabinet text-xl font-bold uppercase tracking-tight mb-2 whitespace-pre-line" style={{ color: accent }}>{thankYouTitle}</p>
            <p className="text-[#A3A3A3] text-sm leading-relaxed whitespace-pre-line">{thankYouBody}</p>
          </div>
        </div>
      </section>

      {/* ─── 13. BORDERS & DIVIDERS ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <RotateCcw className="w-4 h-4" style={{ color: accent }} /> 13. Borders & Dividers
        </h3>
        <p className="text-[10px] text-[#666] mb-3">Style for section dividers, card outlines, and panel edges.</p>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] text-[#888] uppercase tracking-wider block mb-1">Border Style</label>
            <select value={borderStyle.style || 'solid'}
              onChange={(e) => setBorderStyle({ ...borderStyle, style: e.target.value })}
              className="w-full bg-[#141414] border border-[#262626] text-white text-xs px-2 py-2 outline-none"
              style={{ borderRadius: `${radius}px` }}
              data-testid="border-style">
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
              <option value="double">Double</option>
              <option value="neon">Neon Glow</option>
            </select>
          </div>
          <div>
            <div className="flex justify-between items-center">
              <label className="text-[10px] text-[#888] uppercase tracking-wider">Thickness</label>
              <span className="text-[10px] text-[#DAA520] font-mono">{borderStyle.thickness ?? 1}px</span>
            </div>
            <input type="range" min="0" max="6" value={borderStyle.thickness ?? 1}
              onChange={(e) => setBorderStyle({ ...borderStyle, thickness: parseInt(e.target.value) })}
              className="w-full mt-1 accent-[#DAA520] h-2"
              data-testid="border-thickness" />
          </div>
          <div>
            <label className="text-[10px] text-[#888] uppercase tracking-wider block mb-1">Border Color</label>
            <div className="flex items-center gap-1.5">
              <input type="color" value={borderStyle.color || '#262626'}
                onChange={(e) => setBorderStyle({ ...borderStyle, color: e.target.value })}
                className="w-9 h-9 rounded cursor-pointer bg-transparent border-0"
                data-testid="border-color" />
              <input type="text" value={borderStyle.color || '#262626'}
                onChange={(e) => setBorderStyle({ ...borderStyle, color: e.target.value })}
                className="flex-1 bg-[#141414] border border-[#262626] px-2 py-2 text-[10px] font-mono text-white outline-none w-0"
                style={{ borderRadius: `${radius}px` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between items-center">
              <label className="text-[10px] text-[#888] uppercase tracking-wider">Card Corner Radius</label>
              <span className="text-[10px] text-[#DAA520] font-mono">{borderStyle.card_radius ?? 0}px</span>
            </div>
            <input type="range" min="0" max="48" value={borderStyle.card_radius ?? 0}
              onChange={(e) => setBorderStyle({ ...borderStyle, card_radius: parseInt(e.target.value) })}
              className="w-full mt-1 accent-[#DAA520] h-2"
              data-testid="border-card-radius" />
          </div>
        </div>

        {/* Live preview */}
        <div className="p-3 bg-black/40 flex items-center justify-center gap-4" style={{ borderRadius: `${radius}px` }}>
          <div style={{
            width: 100,
            height: 60,
            background: '#141414',
            border: `${borderStyle.thickness ?? 1}px ${borderStyle.style === 'neon' ? 'solid' : (borderStyle.style || 'solid')} ${borderStyle.color || '#262626'}`,
            borderRadius: `${borderStyle.card_radius ?? 0}px`,
            boxShadow: borderStyle.style === 'neon' ? `0 0 10px ${borderStyle.color}, 0 0 2px ${borderStyle.color}` : 'none',
          }} className="flex items-center justify-center text-[10px] text-white">Card</div>
          <div style={{
            height: `${borderStyle.thickness ?? 1}px`,
            flex: 1,
            background: borderStyle.style === 'neon' ? (borderStyle.color || '#262626') : 'transparent',
            borderBottom: borderStyle.style !== 'neon' ? `${borderStyle.thickness ?? 1}px ${borderStyle.style || 'solid'} ${borderStyle.color || '#262626'}` : 'none',
            boxShadow: borderStyle.style === 'neon' ? `0 0 8px ${borderStyle.color}` : 'none',
          }} />
        </div>
      </section>

      {/* ─── 14. SCROLL & SELECTION ─── */}
      <section className="border border-[#1a1a1a] p-4" style={{ borderRadius: `${radius}px` }}>
        <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
          <Eye className="w-4 h-4" style={{ color: primary }} /> 14. Scroll & Selection
        </h3>
        <p className="text-[10px] text-[#666] mb-3">The thin details — scrollbar + the highlight color when visitors select your text.</p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-[#888] uppercase tracking-wider block mb-1">Scrollbar Color</label>
            <div className="flex items-center gap-1.5">
              <input type="color" value={interaction.scroll_color || '#FF3B30'}
                onChange={(e) => setInteraction({ ...interaction, scroll_color: e.target.value })}
                className="w-9 h-9 rounded cursor-pointer bg-transparent border-0"
                data-testid="scroll-color" />
              <input type="text" value={interaction.scroll_color || '#FF3B30'}
                onChange={(e) => setInteraction({ ...interaction, scroll_color: e.target.value })}
                className="flex-1 bg-[#141414] border border-[#262626] px-2 py-2 text-[10px] font-mono text-white outline-none w-0"
                style={{ borderRadius: `${radius}px` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between items-center">
              <label className="text-[10px] text-[#888] uppercase tracking-wider">Scrollbar Width</label>
              <span className="text-[10px] text-[#DAA520] font-mono">{interaction.scroll_width ?? 10}px</span>
            </div>
            <input type="range" min="0" max="24" value={interaction.scroll_width ?? 10}
              onChange={(e) => setInteraction({ ...interaction, scroll_width: parseInt(e.target.value) })}
              className="w-full mt-1 accent-[#DAA520] h-2"
              data-testid="scroll-width" />
          </div>
          <div>
            <label className="text-[10px] text-[#888] uppercase tracking-wider block mb-1">Selection Background</label>
            <div className="flex items-center gap-1.5">
              <input type="color" value={interaction.selection_color || '#FF3B30'}
                onChange={(e) => setInteraction({ ...interaction, selection_color: e.target.value })}
                className="w-9 h-9 rounded cursor-pointer bg-transparent border-0"
                data-testid="selection-color" />
              <input type="text" value={interaction.selection_color || '#FF3B30'}
                onChange={(e) => setInteraction({ ...interaction, selection_color: e.target.value })}
                className="flex-1 bg-[#141414] border border-[#262626] px-2 py-2 text-[10px] font-mono text-white outline-none w-0"
                style={{ borderRadius: `${radius}px` }} />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-[#888] uppercase tracking-wider block mb-1">Selection Text Color</label>
            <div className="flex items-center gap-1.5">
              <input type="color" value={interaction.selection_text || '#FFFFFF'}
                onChange={(e) => setInteraction({ ...interaction, selection_text: e.target.value })}
                className="w-9 h-9 rounded cursor-pointer bg-transparent border-0"
                data-testid="selection-text-color" />
              <input type="text" value={interaction.selection_text || '#FFFFFF'}
                onChange={(e) => setInteraction({ ...interaction, selection_text: e.target.value })}
                className="flex-1 bg-[#141414] border border-[#262626] px-2 py-2 text-[10px] font-mono text-white outline-none w-0"
                style={{ borderRadius: `${radius}px` }} />
            </div>
          </div>
        </div>

        <div className="mt-3 p-3 bg-black/40 border border-[#1a1a1a]" style={{ borderRadius: `${radius}px` }}>
          <p className="text-xs text-[#A3A3A3]">
            <span style={{ background: interaction.selection_color || '#FF3B30', color: interaction.selection_text || '#FFFFFF', padding: '2px 4px' }}>Try selecting</span> this text after saving — your new selection colors will apply site-wide.
          </p>
        </div>
      </section>
    </div>
  );
}
